import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Barcode } from "./Barcode";
import { QrCode } from "./QrCode";
import { CheckCircle2, XCircle, Camera, CameraOff, Loader2, ScanLine } from "lucide-react";

export function ScanValidateDialog({ open, onClose, value }) {
  const code = (value || "RESICETAK").toString();
  const [self, setSelf] = useState("loading"); // "loading" | {ok,text}
  const [cam, setCam] = useState(null);
  const [scanning, setScanning] = useState(false);
  const scannerRef = useRef(null);

  // Auto self-validation: decode the generated QR to confirm it is machine-readable.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSelf("loading");
    setCam(null);
    (async () => {
      try {
        await new Promise((r) => setTimeout(r, 120));
        const dataUrl = await QRCode.toDataURL(code, { margin: 1, width: 400 });
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], "qr.png", { type: "image/png" });
        const h = new Html5Qrcode("hidden-filescan", {
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE], verbose: false,
        });
        const res = await h.scanFile(file, false);
        try { await h.clear(); } catch { /* ignore */ }
        if (!cancelled) setSelf({ ok: res === code, text: res });
      } catch {
        if (!cancelled) setSelf({ ok: false, text: null });
      }
    })();
    return () => { cancelled = true; };
  }, [open, code]);

  const stopCam = async () => {
    try {
      if (scannerRef.current) { await scannerRef.current.stop(); await scannerRef.current.clear(); }
    } catch { /* ignore */ }
    scannerRef.current = null;
    setScanning(false);
  };

  const startCam = async () => {
    setCam(null);
    setScanning(true);
    try {
      const h = new Html5Qrcode("camera-reader", {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE, Html5QrcodeSupportedFormats.CODE_128],
        verbose: false,
      });
      scannerRef.current = h;
      await h.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 },
        (decoded) => setCam({ ok: decoded === code, text: decoded }),
        () => {});
    } catch {
      setScanning(false);
      setCam({ ok: false, text: null, err: true });
    }
  };

  useEffect(() => { if (!open) stopCam(); return () => { stopCam(); }; }, [open]);

  const Result = ({ r }) => {
    if (r === "loading") return <span className="flex items-center gap-1.5 text-slate-500 text-sm"><Loader2 size={15} className="animate-spin" /> Memvalidasi…</span>;
    if (!r) return null;
    return r.ok ? (
      <span className="flex items-center gap-1.5 text-emerald-600 text-sm font-semibold">
        <CheckCircle2 size={16} /> Terbaca & cocok: <span className="font-mono">{r.text}</span>
      </span>
    ) : r.err ? (
      <span className="flex items-center gap-1.5 text-amber-600 text-sm"><CameraOff size={16} /> Kamera tidak tersedia di perangkat ini</span>
    ) : r.text ? (
      <span className="flex items-center gap-1.5 text-amber-600 text-sm"><XCircle size={16} /> Terbaca tapi beda: <span className="font-mono">{r.text}</span></span>
    ) : (
      <span className="flex items-center gap-1.5 text-red-600 text-sm"><XCircle size={16} /> Tidak terbaca</span>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { stopCam(); onClose?.(); } }}>
      <DialogContent className="max-w-md" data-testid="scan-validate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <ScanLine size={20} className="text-sky-600" /> Tes Scan & Validasi
          </DialogTitle>
          <DialogDescription>Pastikan barcode/QR resi terbaca sebelum cetak massal.</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-slate-200 bg-white p-4 flex items-center justify-between gap-3">
          <div className="flex-1 min-w-0"><Barcode value={code} height={46} /></div>
          <QrCode value={code} size={76} />
        </div>

        <div className="rounded-lg bg-slate-50 p-3" data-testid="scan-self-result">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-mono mb-1">Validasi otomatis</div>
          <Result r={self} />
        </div>

        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-mono">Scan pakai kamera (opsional)</div>
          <div id="camera-reader" className={scanning ? "rounded-lg overflow-hidden border border-slate-200" : "hidden"} />
          {cam && <Result r={cam} />}
          {!scanning ? (
            <Button variant="outline" className="w-full gap-2" data-testid="btn-start-camera-scan" onClick={startCam}>
              <Camera size={16} /> Mulai Scan Kamera
            </Button>
          ) : (
            <Button variant="outline" className="w-full gap-2 text-red-600" data-testid="btn-stop-camera-scan" onClick={stopCam}>
              <CameraOff size={16} /> Hentikan Kamera
            </Button>
          )}
        </div>

        <div id="hidden-filescan" style={{ display: "none" }} />
      </DialogContent>
    </Dialog>
  );
}
